import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { LogBookUser, LogUser } from "ng/src/app/users/shared/user.model";
import { BaseProjections, GeneralParameters } from "app/shared/model/request-parameter.model";
import { TemplateSegment } from "ng/src/app/templates/shared/template.model";
import { NoteTemplateValue, NoteTemplateValueSignoff } from "ng/src/app/templates/shared/notetemplatevalue.model";
import { forkJoin, of } from "rxjs";
import { map } from "rxjs/operators";
import { HasId } from "app/shared/model/properties.model";
import { Utils } from "ng/src/app/core/utils";
import { HALResponse } from "ng/src/app/shared/model/app.model";
import { LogService } from "ng/src/app/logs/log.service";
import { SignoffControlNote, SignoffTableModel } from "ng/src/app/notes/signoffs/signoff-controls.model";
import { ShiftsService } from "ng/src/app/shifts/shifts.service";

@Injectable({providedIn: 'root'})
export class SignoffService {
	constructor(
		private http: HttpClient,
		private shiftService: ShiftsService
	) {}
	
	doSignoff(templateValue: NoteTemplateValue, byUser: LogBookUser, type: 'signoff' | 'reject') {
		return this.http.patch(templateValue._links.self.href, {
			booleanValue: type === 'signoff',
			userValue: byUser._links.self.href
		});
	}
	
	undoSignoff(templateValue: NoteTemplateValue, signoff: NoteTemplateValueSignoff) {
		return this.http.patch(templateValue._links.self.href, {
			booleanValue: null,
			userValue: signoff.user._links.self.href
		})
	}
	
	modifySignoffUsers(forNote: SignoffControlNote, segment: TemplateSegment, adding: LogUser[], removing: LogUser[] = []) {
		const mapFunc = (usr: LogUser) => usr.user.id;
		const saveModel = {
			noteTemplateValues: [{
					templateSegmentId: segment.id,
					addToCollections: { signoffUsers: adding.map(mapFunc) },
					removeFromCollections: { signoffUsers: removing.map(mapFunc) }
				}]
		}
		
		return this.http.patch(forNote._links.self.href, saveModel);
	}
	
	getSignoffData(note: SignoffControlNote) {
		const user = LogService.selectedLog!.activeLogUser.user;
		const canWrite = LogService.selectedLog!.role !== 'READ';
		const userId = user.id;
		
		return this.getSignoffComponents(note, userId)
			.pipe(map(map => {
				const models: SignoffTableModel[] = [];
				const levelCompletionMap: Record<number, boolean> = {};
				const levelMap: Record<number, SignoffTableModel[]> = {};
				
				const valuesMap = Utils.arrayToMap(map.templateValues, val => val.templateSegmentId);
				const completedMap = Utils.arrayToMap(map.completed, val => val.id);
				const canSignoffMap = Utils.arrayToMap(map.segmentsUserCanSignoff, val => val.id);
				
				const filterCompleted = (signoffs: NoteTemplateValueSignoff[], users: LogBookUser[]) =>
					users.filter(user => signoffs.findIndex(signoff => signoff.user.id === user.id) === -1);
				
				const getUserString = (pendingFrom: boolean, requiresAll: boolean, users: LogBookUser[]) => {
					const single = users.length === 1;
					const usersStr = users.sort(Utils.createMultiSortFunction([
							[user => user.lastName, 'asc'],
							[user => user.firstName, 'asc']
						]))
						.map(user => `${user.firstName} ${user.lastName}`)
						.join(', ');
					
					return `${pendingFrom ? 'Pending from ' : 'Will require '} ${!single ? ((requiresAll ? 'all' : 'any') + ' of') : ''}
							the following user${single ? '' : 's'}: ${usersStr}`;
				}
				
				map.signoffSegments.forEach(segment => {
					if (!(segment.id in valuesMap)) return;
					
					const level = segment.level ?? 1;
					const value = valuesMap[segment.id]!;
					const data = value.signoffData!;
					
					const completed = value?.id in completedMap;
					const isEditUser = !!segment.editUsers.find(usr => usr.user.id === userId);
					
					levelCompletionMap[level] = (levelCompletionMap[level] ?? completed) && completed;
					
					let lowerLevelsCompleted = true;
					for (let i = level - 1; i >= 1; i--) {
						const val = levelCompletionMap[i];
						if (val === undefined) continue;
						lowerLevelsCompleted = val;
						if (!lowerLevelsCompleted)
							break;
					}
					
					let displayMessage: string = '';
					if (!completed) {
						if (data.allowAnySignoff) {
							displayMessage = `${lowerLevelsCompleted ? 'Pending from' : 'Will require'}: any user`
						}
						else {
							if (data.signoffUsers.length <= 0) {
								displayMessage = 'Awaiting users to be assigned for sign off';
							}
							else {
								displayMessage = getUserString(lowerLevelsCompleted, !!data.requireAllSignoffs,
									filterCompleted(data.signoffs, data.signoffUsers));
							}
						}
					}
					
					const signoffs = data.signoffs.map(signoff => {
							return {
								...signoff,
								canUndo: ((!!segment.allowUndo && signoff.user.id === userId) || isEditUser) && canWrite
							}
						})
						.sort(Utils.createMultiSortFunction([
							[user => user.user.lastName, 'asc'],
							[user => user.user.firstName, 'asc']
						]));
					
					const shiftReqMet = (data.requireOnShift) ? this.shiftService.isOnShift : true;
					
					const model: SignoffTableModel = {
						...data,
						name: segment.name,
						level: level,
						segment: segment,
						templateValue: value,
						completed: completed,
						canSignoff: (!!data.allowAnySignoff || (value.id in canSignoffMap)) && shiftReqMet && canWrite,
						canEditUsers: isEditUser && canWrite && !data.allowAnySignoff,
						hasLowerLevelPending: !lowerLevelsCompleted,
						displayMessage: displayMessage,
						allowsReject: !!segment.allowReject,
						signoffs: signoffs
					}
					
					const levelArr = levelMap[level] ?? [];
					levelArr.push(model);
					levelMap[level] = levelArr;
					
					models.push(model);
				});
				
				models.forEach((model) => {
					let hasHigherLevelSignoffs = false;
					for (let i = model.level + 1; i <= 5; i++) {
						const modelArr = levelMap[i] ?? [];
						hasHigherLevelSignoffs = modelArr.some(m => m.signoffs.length);
						
						if (hasHigherLevelSignoffs)
							break;
					}
					model.canEditUsers = model.canEditUsers && !hasHigherLevelSignoffs;
					model.signoffs.forEach(s => s.canUndo = s.canUndo && (!hasHigherLevelSignoffs));
				});
				
				return models;
			}))
	}
	
	noteNeedsSignoffFromUser(noteId: number, userId?: number) {
		userId = userId ?? LogService.selectedLog?.activeLogUser?.user?.id;
		if (typeof userId !== 'number' || isNaN(userId) || isNaN(noteId)) return of(false);
		return this.getSegmentsUserCanSignoff(userId, noteId)
			.pipe(
				map(segments => segments.length > 0)
			);
	}
	
	private getSignoffComponents(note: SignoffControlNote, userId: number) {
		return forkJoin({
			completed: this.getCompletedSegments(note.id),
			signoffSegments: this.getTemplateSegments(note.templateId!),
			segmentsUserCanSignoff: this.getSegmentsUserCanSignoff(userId, note.id),
			templateValues: this.getNoteTemplateValues(note.id)
		})
	}
	
	private getTemplateSegments(templateId: number) {
		const params: GeneralParameters = {
			template: templateId,
			sort: ['sortOrder,asc'],
			'type.name': 'Signoff',
		}
		return this.http.get<HALResponse<{ templateSegments: TemplateSegment[]}>>('/api/templateSegments', { params: params })
			.pipe(map(res => res._embedded.templateSegments));
	}
	
	private getSegmentsUserCanSignoff(userId: number, noteId: number) {
		const params: NoteTemplateValue.Query = {
			note: noteId,
			signoffPendingFor: userId,
			projection: BaseProjections.IdProjection,
			'templateSegment.type.name': 'Signoff'
		}
		return this.http.get<HALResponse<{ noteTemplateValues: HasId[] }>>('/api/noteTemplateValues', { params: params })
			.pipe(map(res => res._embedded.noteTemplateValues));
	}
	
	private getCompletedSegments(noteId: number) {
		const params: NoteTemplateValue.Query = {
			note: noteId,
			'templateSegment.type.name': 'Signoff',
			signoffIsComplete: true,
			projection: BaseProjections.IdProjection
		}
		return this.http.get<HALResponse<{ noteTemplateValues: HasId[] }>>('/api/noteTemplateValues', { params: params })
			.pipe(map(res => res._embedded.noteTemplateValues));
	}
	
	private getNoteTemplateValues(noteId: Number) {
		const params: NoteTemplateValue.Query = {
			note: noteId,
			'templateSegment.type.name': 'Signoff'
		}
		return this.http.get<HALResponse<{ noteTemplateValues: NoteTemplateValue[] }>>('/api/noteTemplateValues', { params: params })
			.pipe(map(res => res._embedded.noteTemplateValues));
	}
}